import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Plus, Copy, Trash2, Loader2, Sparkles, ChevronDown, Layers, Radio, Users } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/**
 * D1. Bulk Stack Assign toolbar.
 *
 * Floats along the bottom of the Stack Map canvas when the user has
 * Shift-clicked >= 2 platform nodes. Supports:
 *   - Assign product to all selected platforms
 *   - Copy stack section from another property
 *   - Clear the group (delete assignments at those platforms)
 *   - Deselect / cancel
 */
export default function BulkStackToolbar({
  selectedNodes = [],
  selectedProperty,
  properties = [],
  token,
  onDone,
  onDeselect,
}) {
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copyFrom, setCopyFrom] = useState('');
  const [copyMode, setCopyMode] = useState('property'); // 'property' | 'group'
  const [groups, setGroups] = useState([]);
  const [broadcastGroupId, setBroadcastGroupId] = useState('');
  const [broadcastOverwrite, setBroadcastOverwrite] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!productQuery || productQuery.length < 2) { setProductResults([]); return; }
    let cancelled = false;
    axios.get(`${BACKEND}/api/products`, { headers, params: { q: productQuery, limit: 15 } })
      .then(res => { if (!cancelled) setProductResults(res.data.products || []); })
      .catch(() => setProductResults([]));
    return () => { cancelled = true; };
  }, [productQuery, headers]);

  // Load property groups (used by "Copy from group" and "Broadcast to group")
  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND}/api/property-groups`, { headers })
      .then(res => { if (!cancelled) setGroups(res.data.groups || []); })
      .catch(() => setGroups([]));
    return () => { cancelled = true; };
  }, [headers]);

  if (!selectedNodes || selectedNodes.length < 2) return null;

  const assignProduct = async (product) => {
    setBusy(true);
    let ok = 0, skip = 0, err = 0;
    for (const node of selectedNodes) {
      try {
        await axios.post(`${BACKEND}/api/admin/assignments`, {
          property_id: selectedProperty,
          platform_type_id: node.id,
          product_id: product.id,
          vendor_id: product.vendor_id || '',
        }, { headers });
        ok++;
      } catch (e) {
        if (e?.response?.status === 409) skip++;
        else err++;
      }
    }
    setBusy(false);
    toast.success(`Assigned "${product.name}" to ${ok} platform${ok !== 1 ? 's' : ''}` + (skip ? ` · ${skip} already had it` : '') + (err ? ` · ${err} failed` : ''));
    setProductQuery('');
    setProductResults([]);
    if (onDone) onDone();
  };

  const copyStackSection = async () => {
    if (!copyFrom) { toast.error(copyMode === 'group' ? 'Pick a source group first' : 'Pick a source property first'); return; }
    setBusy(true);
    try {
      // Resolve the source property. For group mode, pick the first property
      // in the group (typically the flagship / anchor property).
      let sourcePropertyId = copyFrom;
      let sourceLabel = '';
      if (copyMode === 'group') {
        const g = groups.find(gg => gg.id === copyFrom);
        const pid = (g?.property_ids || [])[0];
        if (!pid) { toast.error('That group has no properties'); setBusy(false); return; }
        sourcePropertyId = pid;
        sourceLabel = g?.name || 'group';
      } else {
        sourceLabel = properties.find(p => p.id === copyFrom)?.name || 'property';
      }
      const src = await axios.get(`${BACKEND}/api/stack`, { headers, params: { property_id: sourcePropertyId } });
      const nodeIds = new Set(selectedNodes.map(n => n.id));
      const solutions = [];
      for (const n of (src.data.nodes || [])) {
        if (!nodeIds.has(n.id)) continue;
        for (const s of (n.solutions || [])) {
          if (s.is_shadow) continue;
          if (s.product_id) solutions.push({ platform_type_id: n.id, product_id: s.product_id, vendor_id: '' });
        }
      }
      let ok = 0, err = 0;
      for (const s of solutions) {
        try {
          await axios.post(`${BACKEND}/api/admin/assignments`, { ...s, property_id: selectedProperty }, { headers });
          ok++;
        } catch (e) { if (e?.response?.status !== 409) err++; }
      }
      toast.success(`Copied ${ok} assignment${ok !== 1 ? 's' : ''} from ${sourceLabel}` + (err ? ` · ${err} failed` : ''));
    } catch (e) {
      toast.error('Could not fetch source stack');
    } finally {
      setBusy(false);
      if (onDone) onDone();
    }
  };

  const broadcastToGroup = async () => {
    if (!broadcastGroupId) { toast.error('Pick a target group first'); return; }
    const g = groups.find(gg => gg.id === broadcastGroupId);
    const targetCount = (g?.property_ids || []).filter(pid => pid !== selectedProperty).length;
    if (!targetCount) { toast.error('That group has no other target properties'); return; }
    const modeLabel = broadcastOverwrite ? 'OVERWRITE existing assignments' : 'skip slots that already have assignments';
    if (!window.confirm(`Broadcast the current property's stack (${selectedNodes.length} selected platform${selectedNodes.length !== 1 ? 's' : ''}) to ${targetCount} propert${targetCount === 1 ? 'y' : 'ies'} in "${g?.name}"?\n\nMode: ${modeLabel}.`)) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${BACKEND}/api/admin/property-groups/${broadcastGroupId}/broadcast-stack`,
        {
          source_property_id: selectedProperty,
          platform_type_ids: selectedNodes.map(n => n.id),
          overwrite: broadcastOverwrite,
        },
        { headers },
      );
      toast.success(
        `Broadcast to ${res.data.target_properties} propert${res.data.target_properties === 1 ? 'y' : 'ies'} · ${res.data.created} new`
        + (res.data.overwritten ? ` · ${res.data.overwritten} overwritten` : '')
        + (res.data.skipped_existing ? ` · ${res.data.skipped_existing} skipped` : '')
      );
      setBroadcastGroupId('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Broadcast failed');
    } finally {
      setBusy(false);
      if (onDone) onDone();
    }
  };

  const clearGroup = async () => {
    if (!window.confirm(`Delete all assignments across ${selectedNodes.length} selected platforms at this property?`)) return;
    setBusy(true);
    // Fetch current assignments for the property, then delete each row that
    // matches (platform_type_id ∈ selectedNodes) and belongs to this property.
    try {
      const res = await axios.get(`${BACKEND}/api/stack`, { headers, params: { property_id: selectedProperty } });
      const nodeIds = new Set(selectedNodes.map(n => n.id));
      const ids = [];
      for (const n of (res.data.nodes || [])) {
        if (!nodeIds.has(n.id)) continue;
        for (const s of (n.solutions || [])) {
          if (s.is_shadow) continue;
          if (s.assignment_id) ids.push(s.assignment_id);
        }
      }
      let ok = 0, err = 0;
      for (const id of ids) {
        try {
          await axios.delete(`${BACKEND}/api/admin/assignments/${id}`, { headers });
          ok++;
        } catch (e) { err++; }
      }
      toast.success(`Cleared ${ok} assignment${ok !== 1 ? 's' : ''}` + (err ? ` · ${err} failed` : ''));
    } catch (e) {
      toast.error('Clear failed');
    } finally {
      setBusy(false);
      if (onDone) onDone();
    }
  };

  return (
    <div
      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 max-w-[860px] w-[calc(100%-2rem)] rounded-2xl border bg-card/95 backdrop-blur shadow-2xl px-4 py-3"
      data-testid="bulk-stack-toolbar"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative">
            <Layers className="h-4 w-4 text-violet-600" />
            <span className="absolute -top-1.5 -right-2 bg-violet-600 text-white text-[9px] font-bold rounded-full px-1 h-4 min-w-[16px] inline-flex items-center justify-center">{selectedNodes.length}</span>
          </div>
          <div className="text-xs font-semibold">{selectedNodes.length} platforms selected</div>
        </div>
        <span className="h-4 w-px bg-border" />

        {/* Assign product */}
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" data-testid="bulk-assign-trigger" disabled={busy}>
              <Plus className="h-3.5 w-3.5" /> Assign product…
              <ChevronDown className="h-3 w-3 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-2" data-testid="bulk-assign-popover">
            <Input
              autoFocus
              placeholder="Search products…"
              value={productQuery}
              onChange={e => setProductQuery(e.target.value)}
              className="h-8 text-xs mb-2"
              data-testid="bulk-assign-search"
            />
            <div className="max-h-64 overflow-y-auto space-y-0.5">
              {productResults.length === 0 && productQuery.length >= 2 && (
                <div className="text-xs text-muted-foreground p-3 text-center">No matches</div>
              )}
              {productResults.map(p => (
                <button
                  key={p.id}
                  onClick={() => assignProduct(p)}
                  className="w-full text-left px-2 py-1.5 rounded-md hover:bg-muted text-xs"
                  data-testid={`bulk-assign-option-${p.id}`}
                >
                  <div className="font-medium">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground">{p.vendor_name || 'No vendor'} · {(p.platforms || []).map(pl => pl.name).join(', ') || p.platform_name}</div>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Copy from */}
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" data-testid="bulk-copy-trigger" disabled={busy}>
              <Copy className="h-3.5 w-3.5" /> Copy from…
              <ChevronDown className="h-3 w-3 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-0" data-testid="bulk-copy-popover">
            <div className="flex border-b">
              <button
                type="button"
                onClick={() => { setCopyMode('property'); setCopyFrom(''); }}
                className={`flex-1 text-xs font-medium py-2 px-3 flex items-center justify-center gap-1.5 ${copyMode === 'property' ? 'border-b-2 border-violet-500 text-violet-700' : 'text-muted-foreground hover:bg-muted/40'}`}
                data-testid="bulk-copy-mode-property"
              >
                <Layers className="h-3 w-3" /> Property
              </button>
              <button
                type="button"
                onClick={() => { setCopyMode('group'); setCopyFrom(''); }}
                className={`flex-1 text-xs font-medium py-2 px-3 flex items-center justify-center gap-1.5 ${copyMode === 'group' ? 'border-b-2 border-violet-500 text-violet-700' : 'text-muted-foreground hover:bg-muted/40'}`}
                data-testid="bulk-copy-mode-group"
              >
                <Users className="h-3 w-3" /> Group
              </button>
            </div>
            <div className="p-1 max-h-64 overflow-y-auto">
              {copyMode === 'property' ? (
                <>
                  {properties.filter(p => p.id !== selectedProperty).length === 0 && (
                    <div className="text-xs text-muted-foreground p-3 text-center">No other properties to copy from</div>
                  )}
                  {properties.filter(p => p.id !== selectedProperty).map(p => (
                    <button
                      key={p.id}
                      onClick={() => { setCopyFrom(p.id); }}
                      className={`w-full text-left px-2 py-1.5 rounded-md text-xs ${copyFrom === p.id ? 'bg-violet-100 text-violet-700 font-semibold' : 'hover:bg-muted'}`}
                      data-testid={`bulk-copy-property-${p.id}`}
                    >
                      {p.name}
                    </button>
                  ))}
                </>
              ) : (
                <>
                  {groups.length === 0 && (
                    <div className="text-xs text-muted-foreground p-3 text-center">No property groups configured yet</div>
                  )}
                  {groups.map(g => {
                    const pids = g.property_ids || [];
                    const anchor = pids[0];
                    const disabled = pids.length === 0;
                    return (
                      <button
                        key={g.id}
                        disabled={disabled}
                        onClick={() => setCopyFrom(g.id)}
                        className={`w-full text-left px-2 py-1.5 rounded-md text-xs ${copyFrom === g.id ? 'bg-violet-100 text-violet-700 font-semibold' : 'hover:bg-muted'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid={`bulk-copy-group-${g.id}`}
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full shrink-0" style={{ background: g.color || '#8b5cf6' }} />
                          <span className="flex-1 truncate">{g.name}</span>
                          <span className="text-[9px] text-muted-foreground font-mono">{pids.length}</span>
                        </div>
                        {anchor && (
                          <div className="text-[9px] text-muted-foreground mt-0.5 pl-3.5 truncate">
                            Anchor: {g.property_names?.[0] || anchor}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </>
              )}
            </div>
            {copyFrom && (
              <div className="p-2 border-t">
                <Button size="sm" className="w-full h-7 text-xs" onClick={copyStackSection} disabled={busy} data-testid="bulk-copy-execute">
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Copy selected platforms
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>

        {/* Broadcast to group */}
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs text-violet-700 border-violet-200 hover:bg-violet-50" data-testid="bulk-broadcast-trigger" disabled={busy}>
              <Radio className="h-3.5 w-3.5" /> Broadcast to group…
              <ChevronDown className="h-3 w-3 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-0" data-testid="bulk-broadcast-popover">
            <div className="p-2 border-b">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Broadcast this property&apos;s stack</div>
              <div className="text-[10px] text-muted-foreground">Selected platforms will be copied to every property in the target group.</div>
            </div>
            <div className="p-1 max-h-56 overflow-y-auto">
              {groups.length === 0 && (
                <div className="text-xs text-muted-foreground p-3 text-center">No property groups configured yet</div>
              )}
              {groups.map(g => {
                const targetCount = (g.property_ids || []).filter(pid => pid !== selectedProperty).length;
                const disabled = targetCount === 0;
                return (
                  <button
                    key={g.id}
                    disabled={disabled}
                    onClick={() => setBroadcastGroupId(g.id)}
                    className={`w-full text-left px-2 py-1.5 rounded-md text-xs ${broadcastGroupId === g.id ? 'bg-violet-100 text-violet-700 font-semibold' : 'hover:bg-muted'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid={`bulk-broadcast-group-${g.id}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: g.color || '#8b5cf6' }} />
                      <span className="flex-1 truncate">{g.name}</span>
                      <span className="text-[9px] text-muted-foreground font-mono">{targetCount}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            {broadcastGroupId && (
              <div className="p-2 border-t space-y-2">
                <label className="flex items-center gap-2 text-[11px] cursor-pointer" data-testid="bulk-broadcast-overwrite-label">
                  <input
                    type="checkbox"
                    checked={broadcastOverwrite}
                    onChange={e => setBroadcastOverwrite(e.target.checked)}
                    className="rounded"
                    data-testid="bulk-broadcast-overwrite"
                  />
                  <span>Overwrite existing assignments at target slots</span>
                </label>
                <Button size="sm" className="w-full h-7 text-xs" onClick={broadcastToGroup} disabled={busy} data-testid="bulk-broadcast-execute">
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Radio className="h-3 w-3" />} Broadcast now
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>

        {/* Clear */}
        <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs text-rose-600 border-rose-200 hover:bg-rose-50" onClick={clearGroup} disabled={busy} data-testid="bulk-clear">
          <Trash2 className="h-3.5 w-3.5" /> Clear group
        </Button>

        <div className="ml-auto flex items-center gap-1">
          {busy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={onDeselect} title="Deselect all" data-testid="bulk-deselect">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="mt-1.5 text-[10px] text-muted-foreground truncate">
        {selectedNodes.map(n => n.name).join(' · ')}
      </div>
    </div>
  );
}
